import {
  CustomRoleDetailsModel,
  CustomRoleStore,
  CustomRoleTableUserRow,
  InviteUserToCustomRoleModel,
} from "./customRoleDomain";
import { observer } from "mobx-react";
import { BottomSpacer, Box, Card, Container, Grid, GridItem, VBox } from "../../components-v2/Box";
import {
  useDispatch,
  useLanguage,
  useRootStore,
  useTranslation,
} from "../../stores/RootStoreContext";
import { isMobileView } from "../../utils/screen";
import { MTextInput } from "../../components-v2/TextInput";
import { MSelect } from "../../components-v2/Select";
import { Button, IconButton, LinkButton } from "../../components-v2/Button";
import { Dialog } from "../../components-v2/Dialog";
import { Markdown } from "../../text/Markdown";
import { Checkbox } from "../../components-v2/Checkbox";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { HoverTooltip } from "../../components-v2/Tooltip";
import { CompanyItem, isOk } from "../../common/common";
import { LastLogin } from "../../common/commonComponents";
import { DropdownMenu } from "../../components-v2/Dropdown";
import {
  extractPortfolioCustomRoleUrl,
  PortfolioType,
  SelectedPortfolioOption,
  usePortfolio,
} from "../portfolioDomain";
import { FullScreenLayout } from "../../components-v2/FullScreenLayout";
import { Fragment, useEffect, useMemo } from "react";
import { navigate } from "../../events/eventsDomain";
import { EmptyState } from "../../components-v2/EmptyState";
import { Icon } from "../../components-v2/Icon";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";

export const CustomRoleView = observer(({ store }: { store: CustomRoleStore }) => {
  const { t } = useTranslation();
  const tableColumns: TableColumn<CustomRoleTableUserRow>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
    },
    {
      name: "company",
      label: t("company"),
      align: "left",
      getValue: (row) => <CustomRoleCompaniesData companies={row.companies} />,
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
    },
    {
      name: "lastLogin",
      label: t("last-login"),
      align: "left",
      getValue: (row) => <LastLogin lastLogin={row.lastLogin} />,
    },
    {
      name: "menu",
      label: "",
      align: "right",
      getValue: (row) => (
        <CustomRoleDropdownMenu
          onNavigatePreview={store.previewCustomRole}
          openRoleDetails={store.roleDetailsDialog}
          openInviteToCustomRole={store.openInviteCustomDialog}
          customRoleName={store.customRoleName}
          navigateToCustomRoleDetails={() => store.navigateToCustomRoleDetails(row.externalId)}
        />
      ),
    },
  ];

  return (
    <>
      {store.showEmptyState ? (
        <CustomRoleEmptyState store={store} />
      ) : (
        <Container padding maxWidth="xl">
          <ActionBar store={store} />
          <DataTable
            tableColumns={tableColumns}
            tableData={store.prepCustomRoleUsers}
            getId={({ externalId }) => externalId}
            onRowClick={(row) => store.navigateToCustomRoleDetails(row.externalId)}
          />
        </Container>
      )}
    </>
  );
});

function CustomRoleCompaniesData({ companies }: { companies: CompanyItem[] }) {
  const { t } = useTranslation();

  if (companies.length === 0) {
    return null;
  }
  return companies.length === 1 ? (
    <Box>
      <div>{companies[0].companyName}</div>
    </Box>
  ) : (
    <Box>
      <HoverTooltip
        content={
          <div>
            {companies.map((c) => (
              <Box key={c.organisationNumber} whitespace="nowrap">
                <div>{c.companyName}</div>
              </Box>
            ))}
          </div>
        }
      >
        {({ targetRef }) => (
          <Box ref={targetRef} className="underline tw-decoration-dotted">
            {t("portfolio.numberOfCompanies", { count: companies.length })}
          </Box>
        )}
      </HoverTooltip>
    </Box>
  );
}

const ActionBar = observer(({ store }: { store: CustomRoleStore }) => {
  const { t } = useTranslation();
  return (
    <Box justifyContent={"space-between"} vertical={isMobileView()} wrap>
      <Box vertical={isMobileView()} alignItems={isMobileView() ? undefined : "center"}>
        <MTextInput
          model={store.customRoleSearchModel}
          icon={"magnifying-glass"}
          placeholder={t("portfolioContacts.nameFilterPlaceholder")}
          type={"search"}
          inline
        />
        <MSelect model={store.companyFilter} />
      </Box>
      <Box vertical={isMobileView()} wrap={!isMobileView()} gap={"xl"}>
        <LinkButton
          label={t("custom-roles.preview")}
          icon={"eye"}
          onClick={store.previewCustomRole}
          loading={store.isLoadingPreview}
        />
        <LinkButton
          label={t("portfolioContacts.custom-roles.details")}
          icon={"info"}
          onClick={store.roleDetailsDialog}
        />
        <Button
          variant={"secondary"}
          icon={"plus"}
          label={t("portfolioContacts.custom-roles-invite", {
            n: store.customRoleName,
          })}
          onClick={store.openInviteCustomDialog}
          loading={store.isLoadingInvite}
        />
      </Box>
    </Box>
  );
});

function CustomRoleDropdownMenu({
  onNavigatePreview,
  openRoleDetails,
  openInviteToCustomRole,
  customRoleName,
  navigateToCustomRoleDetails,
}: {
  onNavigatePreview: () => void;
  openRoleDetails: () => void;
  openInviteToCustomRole: () => void;
  customRoleName: string;
  navigateToCustomRoleDetails: () => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      button={{ type: "dotdotdot" }}
      alignment={"end"}
      items={[
        {
          type: "item",
          label: t("portfolioContacts.custom-roles.details-view-custom-role"),
          icon: "user-list",
          onSelect: navigateToCustomRoleDetails,
        },
        {
          type: "item",
          label: t("custom-roles.preview"),
          icon: "eye",
          onSelect: onNavigatePreview,
        },
        {
          type: "item",
          label: t("portfolioContacts.custom-roles.details"),
          icon: "info",
          onSelect: openRoleDetails,
        },
        {
          type: "item",
          label: t("portfolioContacts.custom-roles-invite", { n: customRoleName }),
          icon: "plus",
          onSelect: openInviteToCustomRole,
        },
      ]}
    />
  );
}

export const RoleDetailsDialog = observer(({ model }: { model: CustomRoleDetailsModel }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("portfolioContacts.custom-roles.details-dialog-title", {
        roleName: model.roleName,
      })}
      cancelLabel={t("close")}
      onCancel={model.onClose}
    >
      <VBox>
        {model.description && (
          <VBox>
            <div className={"subtle"}>
              {t("portfolioContacts.custom-roles.details-dialog-description")}
            </div>
            <div>{model.description}</div>
          </VBox>
        )}
        <VBox marginTop={"md"}>
          <div className={"subtle"}>
            {t("portfolioContacts.custom-roles.details-dialog-permissions")}
          </div>
          <Markdown value={t(model.permissionMessages)} />
        </VBox>
      </VBox>
    </Dialog>
  );
});

export const InviteUserToCustomRoleDialog = observer(
  ({ model }: { model: InviteUserToCustomRoleModel }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolioContacts.custom-roles-invite", { n: model.roleName })}
        cancelLabel={t("cancel")}
        onCancel={model.onCancel}
        submitLabel={t("invite-user.send-invite")}
        onSubmit={model.inviteCustomRole}
        isSubmitDisabled={model.isDisabledSubmit}
        isSubmitting={model.isLoadingInvite}
      >
        <VBox gap={"md"}>
          {model.showEmailField && (
            <MTextInput model={model.email} label={t("invite-user.email")} autofocus />
          )}
          {model.getMessageForAllCompaniesAlreadyShared ? (
            <div>{t(model.getMessageForAllCompaniesAlreadyShared)}</div>
          ) : (
            <VBox gap={"md"}>
              <MSelect model={model.language} label={t("invite-user.choose-language-for-email")} />
              {model.availableInvitationCompanies.length >= 1 && (
                <Box>
                  <LinkButton label={t("select-all")} onClick={model.selectAllCompanies} />
                  {"/"}
                  <LinkButton label={t("portfolio.NONE")} onClick={model.deselectAllCompanies} />
                </Box>
              )}
              {model.availableInvitationCompanies.map((c) => (
                <Checkbox
                  key={c.organisationNumber}
                  label={
                    <Box>
                      <div>{c.companyName}</div>
                      <small className={"subtle"}>{c.organisationNumber}</small>
                    </Box>
                  }
                  value={c.organisationNumber}
                  checked={model.isSelected(c.organisationNumber)}
                  onChange={() => model.toggleSelected(c.organisationNumber)}
                />
              ))}
              {model.createMissingRoleNotice && (
                <Box justifyContent={"space-between"} textAlign={"center"}>
                  <Markdown value={t(model.createMissingRoleNotice)} />
                  {model.hasAdminInCreatorCompany && (
                    <LinkButton
                      label={t("portfolioContacts.custom-roles-invite-enable-all")}
                      onClick={() => model.enableAllCompanies()}
                      loading={model.enablingCompany === "all"}
                    />
                  )}
                </Box>
              )}
              {model.permissionMessages && <div>{t(model.permissionMessages)}</div>}
              {model.otherCompanies &&
                model.otherCompanies.map((p) => (
                  <Box
                    key={p.organisationNumber}
                    justifyContent={"space-between"}
                    textAlign={"center"}
                  >
                    <Checkbox
                      label={
                        <Box>
                          <div>{p.companyName}</div>
                          <small className={"subtle"}>{p.organisationNumber}</small>
                        </Box>
                      }
                      disabled
                    />
                    {model.hasAdminInCreatorCompany && (
                      <LinkButton
                        label={t("portfolioContacts.custom-roles-invite-enable-now")}
                        onClick={() => model.enableCompany(p.organisationNumber)}
                        loading={model.enablingCompany === p.organisationNumber}
                      />
                    )}
                  </Box>
                ))}
            </VBox>
          )}
        </VBox>
      </Dialog>
    );
  },
);

export const CustomRoleDetails = observer(
  ({
    customRoleId,
    externalId,
    portfolioType,
    portfolioId,
  }: {
    customRoleId: string;
    externalId: string;
    portfolioType: PortfolioType;
    portfolioId?: string;
  }) => {
    const { portfolio } = usePortfolio({ portfolioType, portfolioId });
    const { portfolioStore, dispatch, apiService, userStore } = useRootStore();
    const locale = useLanguage();
    useEffect(() => {
      if (!portfolioStore.fetchPortfolioCustomRoleResult) {
        portfolioStore.fetchPortfolioCustomRole(portfolio, portfolioId);
      }
    }, [portfolio, portfolioStore, portfolioId]);

    const store = useMemo<CustomRoleStore | undefined>(() => {
      const data = isOk(portfolioStore.fetchPortfolioCustomRoleResult)
        ? portfolioStore.fetchPortfolioCustomRoleResult.data
        : undefined;
      const item = data?.customRoleItems?.find((r) => r.role.customRoleId === customRoleId);
      if (item) {
        const creatorCompany = userStore.companiesWithAdminAccess.find(
          (adminCompany) => adminCompany.organisationNumber === item.role.orgNo,
        );
        return new CustomRoleStore({
          customRoleItem: item,
          availableCompanies: data?.availableCompanies ?? [],
          dispatch,
          apiService,
          selectedPortfolioOption: portfolio,
          locale,
          showEmailField: false,
          user: userStore.user,
          refreshUserInformation: userStore.refreshUserInformation,
          externalId,
          creatorCompany,
        });
      }
    }, [
      portfolioStore.fetchPortfolioCustomRoleResult,
      customRoleId,
      userStore.companiesWithAdminAccess,
      userStore.user,
      userStore.refreshUserInformation,
      dispatch,
      apiService,
      portfolio,
      locale,
      externalId,
    ]);

    return (
      <FullScreenLayout
        pageHeader={
          <CustomRoleDetailsHeader
            selectedPortfolio={portfolio}
            customRoleId={customRoleId}
            adminInCustomRoleName={store?.userName(externalId)}
          />
        }
        centerPanel={
          store ? (
            <CustomRoleInfoBody externalId={externalId} store={store} />
          ) : (
            <LoadingIndicator />
          )
        }
      ></FullScreenLayout>
    );
  },
);

const CustomRoleDetailsHeader = observer(function CustomRoleDetailsHeader({
  selectedPortfolio,
  customRoleId,
  adminInCustomRoleName,
}: {
  selectedPortfolio: SelectedPortfolioOption;
  customRoleId: string;
  adminInCustomRoleName?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Box
      bg={"default"}
      grow
      alignItems={"center"}
      paddingLeft={"md"}
      paddingRight={"lg"}
      paddingY={"md"}
      borderBottom
    >
      <IconButton
        icon={"x"}
        tooltip={t("back")}
        onClick={() =>
          dispatch([navigate(extractPortfolioCustomRoleUrl(selectedPortfolio, customRoleId))])
        }
      />
      <div>{adminInCustomRoleName}</div>
      <div>-</div>
      <div>{t(selectedPortfolio.label)}</div>
    </Box>
  );
});

const CustomRoleInfoBody = observer(function CustomRoleInfoBody({
  externalId,
  store,
}: {
  externalId: string;
  store: CustomRoleStore;
}) {
  const { t } = useTranslation();

  return (
    <VBox maxWidth="lg" gap={"md"} container marginTop="lg">
      <h2>{store.userName(externalId)}</h2>
      <Card title={t("contacts.admins.details")} alignItems={"start"}>
        <Grid>
          <GridItem xs={12} lg={4}>
            {t("email")}
          </GridItem>
          <GridItem xs={12} lg={8}>
            <Box gap={"md"}>
              <div>{store.userEmail(externalId)}</div>
              <LinkButton
                label={t("edit")}
                icon={"edit"}
                onClick={() => store.editEmail(externalId)}
              />
            </Box>
          </GridItem>
        </Grid>
      </Card>
      <CustomRoleCompaniesCard store={store} externalId={externalId} />
    </VBox>
  );
});

const CustomRoleCompaniesCard = observer(
  ({ store, externalId }: { store: CustomRoleStore; externalId: string }) => {
    const { t } = useTranslation();
    return (
      <Card
        title={t("portfolioContacts.custom-roles.details-companies-card-title", {
          roleName: store.customRoleName,
          count: store.selectedUserCompanyItems.length,
        })}
        action={
          store.selectedUserCompanyItems.length > 1 ? (
            <LinkButton
              color={"error"}
              label={t("contacts.admins.revoke-all")}
              onClick={() => store.revoke(externalId)}
              loading={store.revokingAccess === "all"}
            />
          ) : undefined
        }
      >
        {store.selectedUserCompanyItems.map((c) => (
          <Fragment key={c.organisationNumber}>
            <Box vertical={isMobileView()} marginTop={"md"}>
              <div className={"grow"}>{c.companyName}</div>
              <Box gap={"md"} alignItems={"end"}>
                <LinkButton
                  label={t("contacts.revoke")}
                  icon={"trash"}
                  onClick={() => store.revoke(externalId, c.organisationNumber)}
                  loading={store.revokingAccess === c.organisationNumber}
                />
              </Box>
            </Box>
            <hr />
          </Fragment>
        ))}
        <Box marginTop={"md"}>
          <LinkButton
            label={t("portfolio.welcome.add-company")}
            icon={"plus"}
            onClick={store.openInviteCustomDialog}
          />
        </Box>
      </Card>
    );
  },
);

const CustomRoleEmptyState = observer(({ store }: { store: CustomRoleStore }) => {
  const { t } = useTranslation();
  return (
    <VBox grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<Icon name={"user-list"} duotone size={"mega"} />}
        title={store.customRoleName}
        description={store.description}
        actions={[
          <Button
            key={"custom-role-invite"}
            variant={"secondary"}
            icon={"plus"}
            label={t("portfolioContacts.custom-roles-invite", {
              n: store.customRoleName,
            })}
            onClick={store.openInviteCustomDialog}
            loading={store.isLoadingInvite}
          />,
          <LinkButton
            key={"custom-role-preview"}
            label={t("custom-roles.preview")}
            icon={"eye"}
            onClick={store.previewCustomRole}
            loading={store.isLoadingPreview}
          />,
          <LinkButton
            key={"custom-role-details"}
            label={t("portfolioContacts.custom-roles.details")}
            icon={"info"}
            onClick={store.roleDetailsDialog}
          />,
        ]}
      />
      <BottomSpacer />
    </VBox>
  );
});
